"use client";

import { format } from "date-fns";
import { useTranslations } from "next-intl";

import DetailRow from "@/components/detail-row";
import FormattedCurrencyAmount from "@/components/formatted-currency-amount";
import { LEDGER_ENTRY_DATE_TIME_POSTINGS_FORMAT } from "@/constants/dateTime";

/**
 * Renders the subset of fields that are guaranteed to exist on every
 * `LedgerEntry` — independent of any journal-specific `resolvedData`.
 *
 * Used as the fallback body when a detail component is invoked with
 * `entry.resolvedData === null` (the BFF couldn't resolve the journal
 * payload). The customer still sees what we know: amount + direction,
 * value date, comment, and the row identifier. The `MissingResolutionAlert`
 * is rendered alongside this to direct the user to support.
 */
export default function LedgerEntryBaseFields({
  entry,
}: {
  entry: LedgerEntryResolvedType;
}) {
  const tLedgerFields = useTranslations("ledger.fields");

  const isCredit = entry.credit > 0;
  const isDebit = entry.debit > 0;
  const amount = isCredit ? entry.credit : entry.debit;
  const amountLabel = isCredit
    ? tLedgerFields("amountReceived")
    : tLedgerFields("amountSent");

  return (
    <div className="space-y-0">
      {(isCredit || isDebit) && (
        <DetailRow
          label={amountLabel}
          value={
            <FormattedCurrencyAmount
              balance={amount}
              product={entry.product}
              forceNegative={isDebit}
              forcePositive={isCredit}
            />
          }
          translateValue={false}
        />
      )}

      {entry.valueDate && (
        <DetailRow
          label={tLedgerFields("valueDate")}
          value={format(
            new Date(entry.valueDate),
            LEDGER_ENTRY_DATE_TIME_POSTINGS_FORMAT
          )}
        />
      )}

      {entry.comment && (
        <DetailRow
          label={tLedgerFields("comment")}
          value={entry.comment}
          textClassName="max-w-[300px]"
          translateValue={false}
        />
      )}

      <hr className="my-2 opacity-65" />

      <DetailRow
        textClassName="font-mono text-sm justify-end flex-1 items-end"
        label={tLedgerFields("referenceId")}
        value={entry.uuid}
        translateValue={false}
      />
    </div>
  );
}
